import { BRAND } from "../risikoanalyseStep/tokens";
import type { ImmoStepKind } from "./types";

const W = 130;
const H = 90;

const Wrap = ({ children }: { children: React.ReactNode }) => (
  <svg width={W} height={H} viewBox="0 0 140 96" fill="none">
    {children}
  </svg>
);

const Anschrift = () => (
  <Wrap>
    <rect x="14" y="14" width="60" height="68" rx="3" fill={BRAND.bg} stroke={BRAND.purple} strokeWidth="2" />
    <path d="M14 32h60" stroke={BRAND.muted} strokeWidth="1.5" />
    <path d="M22 44h26M22 54h32M22 64h22" stroke={BRAND.muted} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="100" cy="44" r="14" fill={BRAND.gold} />
    <path d="M100 36c-3 0-5 2-5 5 0 4 5 9 5 9s5-5 5-9c0-3-2-5-5-5zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" fill={BRAND.ink} />
    <path d="M86 78l28-12" stroke={BRAND.purple} strokeWidth="1.5" strokeDasharray="3 3" />
  </Wrap>
);

const Gebaeudeart = () => (
  <Wrap>
    {[0, 1].map((row) =>
      [0, 1, 2].map((col) => {
        const x = 18 + col * 36;
        const y = 14 + row * 36;
        const accent = (row + col) % 2 === 0 ? BRAND.purple : BRAND.gold;
        return (
          <rect
            key={`${row}-${col}`}
            x={x}
            y={y}
            width={28}
            height={28}
            rx={3}
            fill={accent}
            opacity={(row + col) % 3 === 0 ? 1 : 0.85}
          />
        );
      })
    )}
  </Wrap>
);

const Spezifizierung = () => (
  <Wrap>
    <rect x="20" y="16" width="60" height="68" fill={BRAND.bg} stroke={BRAND.purple} strokeWidth="2" />
    <path d="M20 30h60M20 44h60M20 58h60M20 72h60" stroke={BRAND.purple} strokeOpacity="0.3" strokeWidth="1" strokeDasharray="2 2" />
    <path d="M86 18v66l8-4 8 4-8-4-8 4z" fill={BRAND.gold} />
    <line x1="86" y1="14" x2="86" y2="86" stroke={BRAND.ink} strokeWidth="1.5" />
    <line x1="102" y1="14" x2="102" y2="86" stroke={BRAND.ink} strokeWidth="1.5" />
  </Wrap>
);

// Die Motive „Leerstand", „EinheitenDetail" und „Kunst" sind am 19.08.2026
// entfallen: ihre Schritte sind in „Bauart" bzw. „Anzahl der Einheiten"
// aufgegangen, und ein Motiv ohne Schritt ist toter Code.

const Dach = () => (
  <Wrap>
    <path d="M14 60l16-22 16 22z" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M52 60l16-12 16 12z" fill={BRAND.purple} stroke={BRAND.ink} strokeWidth="1.5" strokeLinejoin="round" opacity="0.85" />
    <path d="M90 60h36" stroke={BRAND.ink} strokeWidth="3" strokeLinecap="round" />
    <path d="M14 60h112" stroke={BRAND.ink} strokeWidth="2" />
    <rect x="14" y="60" width="32" height="20" fill={BRAND.bg} stroke={BRAND.ink} strokeWidth="1.5" />
    <rect x="52" y="60" width="32" height="20" fill={BRAND.bg} stroke={BRAND.ink} strokeWidth="1.5" />
    <rect x="90" y="60" width="36" height="20" fill={BRAND.bg} stroke={BRAND.ink} strokeWidth="1.5" />
  </Wrap>
);

const EinheitenAnzahl = () => (
  <Wrap>
    <rect x="22" y="20" width="96" height="60" fill={BRAND.bg} stroke={BRAND.purple} strokeWidth="2" />
    {[0, 1, 2].map((row) =>
      [0, 1, 2, 3].map((col) => (
        <rect
          key={`${row}-${col}`}
          x={28 + col * 22}
          y={26 + row * 18}
          width="16"
          height="12"
          fill={(row * 4 + col) % 5 === 0 ? BRAND.gold : BRAND.purple}
          opacity={(row * 4 + col) % 3 === 0 ? 1 : 0.5}
        />
      ))
    )}
  </Wrap>
);

const Branchen = () => (
  <Wrap>
    <path d="M16 80V40l16-12 16 12v40h-12V60h-8v20H16z" fill={BRAND.purple} />
    <rect x="56" y="50" width="22" height="30" fill={BRAND.gold} />
    <rect x="60" y="56" width="6" height="6" fill={BRAND.ink} />
    <rect x="68" y="56" width="6" height="6" fill={BRAND.ink} />
    <path d="M86 80V40h28l8 8v32H86z" fill={BRAND.purple} opacity="0.85" />
    <rect x="92" y="48" width="8" height="10" fill={BRAND.bg} />
    <rect x="104" y="48" width="8" height="10" fill={BRAND.bg} />
  </Wrap>
);

const Aussenanlagen = () => (
  <Wrap>
    <ellipse cx="70" cy="78" rx="56" ry="8" fill={BRAND.pillarGreen} opacity="0.25" />
    <path d="M30 78c0-14 8-22 16-22s8 8 8 14M50 78V60M86 78c0-14 8-22 16-22s8 8 8 14M106 78V60" stroke={BRAND.pillarGreen} strokeWidth="2" strokeLinecap="round" fill="none" />
    <circle cx="36" cy="50" r="10" fill={BRAND.pillarGreen} />
    <circle cx="92" cy="50" r="10" fill={BRAND.pillarGreen} />
    <path d="M64 78V40h12v38" stroke={BRAND.gold} strokeWidth="2" fill="none" />
  </Wrap>
);

const Bauausfuehrung = () => (
  <Wrap>
    <rect x="20" y="30" width="48" height="50" fill={BRAND.purple} />
    <rect x="72" y="44" width="44" height="36" fill={BRAND.gold} />
    <rect x="28" y="38" width="8" height="10" fill={BRAND.bg} />
    <rect x="40" y="38" width="8" height="10" fill={BRAND.bg} />
    <rect x="52" y="38" width="8" height="10" fill={BRAND.bg} />
    <rect x="28" y="54" width="8" height="10" fill={BRAND.bg} />
    <rect x="40" y="54" width="8" height="10" fill={BRAND.bg} />
    <rect x="52" y="54" width="8" height="10" fill={BRAND.bg} />
    <rect x="80" y="52" width="10" height="12" fill={BRAND.bg} />
    <rect x="96" y="52" width="10" height="12" fill={BRAND.bg} />
  </Wrap>
);

const Sicherheit = () => (
  <Wrap>
    <path d="M70 14l32 12v22c0 18-14 32-32 38-18-6-32-20-32-38V26l32-12z" fill={BRAND.purple} />
    <path d="M58 50l8 8 18-18" stroke={BRAND.gold} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </Wrap>
);

/**
 * „Schadenprävention" — ein Tropfen über einem Melder, der ihn früh bemerkt.
 * Bewusst nicht das Schild der „Sicherheit": dort geht es um Anlagen, die den
 * Schaden begrenzen, hier um Systeme, die ihn ankündigen.
 */
const Praevention = () => (
  <Wrap>
    <path d="M70 14c-10 14-16 22-16 30a16 16 0 0 0 32 0c0-8-6-16-16-30z" fill={BRAND.purple} />
    <path d="M64 44c0 6 3 9 6 10" stroke={BRAND.bg} strokeWidth="2" strokeLinecap="round" fill="none" />
    <rect x="46" y="68" width="48" height="14" rx="7" fill={BRAND.ink} />
    <circle cx="70" cy="75" r="4" fill={BRAND.gold} />
    <path d="M34 76c0-8 4-15 10-19M106 76c0-8-4-15-10-19" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M18 76c0-13 6-24 15-30M122 76c0-13-6-24-15-30" stroke={BRAND.gold} strokeOpacity="0.45" strokeWidth="2" strokeLinecap="round" fill="none" />
  </Wrap>
);

const Energie = () => (
  <Wrap>
    <path d="M70 14c-12 0-22 10-22 22 0 8 6 14 10 18 2 2 4 4 4 8h16c0-4 2-6 4-8 4-4 10-10 10-18 0-12-10-22-22-22z" fill={BRAND.gold} />
    <rect x="60" y="68" width="20" height="6" fill={BRAND.ink} />
    <rect x="62" y="76" width="16" height="4" fill={BRAND.ink} />
  </Wrap>
);

const Umwelt = () => (
  <Wrap>
    <path d="M70 16c-22 0-32 14-32 30 0 18 16 32 32 32 16 0 32-14 32-32 0-16-10-30-32-30z" fill={BRAND.pillarGreen} />
    <path d="M70 30v44M52 50q18-14 36 0M88 60q-18 14-36 0" stroke={BRAND.bg} strokeWidth="2" fill="none" strokeLinecap="round" />
  </Wrap>
);

const Anbauten = () => (
  <Wrap>
    <path d="M14 80V40l24-22 24 22v40H14z" fill={BRAND.purple} />
    <rect x="62" y="50" width="32" height="30" fill={BRAND.gold} />
    <rect x="22" y="54" width="10" height="10" fill={BRAND.bg} />
    <rect x="44" y="54" width="10" height="10" fill={BRAND.bg} />
    <circle cx="106" cy="36" r="12" fill={BRAND.ink} />
    <path d="M106 30v12M100 36h12" stroke={BRAND.bg} strokeWidth="2.5" strokeLinecap="round" />
  </Wrap>
);

/** „Eigentum & Rechte" — Schlüssel plus Urkunde. */
const Eigentum = () => (
  <Wrap>
    <circle cx="40" cy="58" r="4" fill={BRAND.gold} />
    <path d="M28 58h44" stroke={BRAND.gold} strokeWidth="3" strokeLinecap="round" />
    <rect x="68" y="48" width="20" height="20" rx="2" fill={BRAND.purple} />
    <path d="M72 56h12M72 60h12" stroke={BRAND.gold} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="36" cy="58" r="6" fill="none" stroke={BRAND.purple} strokeWidth="2" />
  </Wrap>
);

/** „Beteiligte am Objekt" — drei Personen, über Linien mit dem Objekt verbunden. */
const Beteiligte = () => (
  <Wrap>
    <path d="M70 26L86 40v22H54V40z" fill={BRAND.bg} stroke={BRAND.purple} strokeWidth="2" strokeLinejoin="round" />
    <rect x="64" y="48" width="12" height="14" fill={BRAND.gold} />
    <path d="M70 62v10M70 72l-32 8M70 72l32 8" stroke={BRAND.purple} strokeWidth="1.5" strokeDasharray="3 3" />
    <circle cx="26" cy="80" r="7" fill={BRAND.purple} />
    <path d="M17 92c1-5 4-7 9-7s8 2 9 7z" fill={BRAND.purple} opacity="0.7" />
    <circle cx="70" cy="80" r="7" fill={BRAND.gold} />
    <path d="M61 92c1-5 4-7 9-7s8 2 9 7z" fill={BRAND.gold} opacity="0.7" />
    <circle cx="114" cy="80" r="7" fill={BRAND.purple} />
    <path d="M105 92c1-5 4-7 9-7s8 2 9 7z" fill={BRAND.purple} opacity="0.7" />
  </Wrap>
);

const Unterlagen = () => (
  <Wrap>
    <rect x="32" y="14" width="56" height="68" rx="3" fill={BRAND.bg} stroke={BRAND.purple} strokeWidth="2" />
    <rect x="38" y="22" width="44" height="4" rx="2" fill={BRAND.muted} opacity="0.4" />
    <rect x="38" y="32" width="44" height="4" rx="2" fill={BRAND.muted} opacity="0.4" />
    <rect x="38" y="42" width="32" height="4" rx="2" fill={BRAND.muted} opacity="0.4" />
    <circle cx="96" cy="64" r="14" fill={BRAND.gold} />
    <path d="M96 56v16M88 64h16" stroke={BRAND.ink} strokeWidth="3" strokeLinecap="round" />
  </Wrap>
);

const Summary = () => (
  <Wrap>
    <rect x="20" y="14" width="100" height="72" rx="6" fill={BRAND.white} stroke={BRAND.purple} strokeWidth="2" />
    <line x1="34" y1="32" x2="86" y2="32" stroke={BRAND.muted} strokeWidth="2" strokeLinecap="round" />
    <line x1="34" y1="46" x2="100" y2="46" stroke={BRAND.muted} strokeWidth="2" strokeLinecap="round" />
    <line x1="34" y1="60" x2="76" y2="60" stroke={BRAND.muted} strokeWidth="2" strokeLinecap="round" />
    <circle cx="106" cy="68" r="14" fill={BRAND.gold} />
    <path d="M99 68 l5 5 l8 -10" stroke={BRAND.ink} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </Wrap>
);

const HEROES: Record<ImmoStepKind, () => JSX.Element> = {
  anschrift: Anschrift,
  gebaeudeart: Gebaeudeart,
  spezifizierung: Spezifizierung,
  dach: Dach,
  einheiten_anzahl: EinheitenAnzahl,
  branchen: Branchen,
  aussenanlagen: Aussenanlagen,
  bauausfuehrung: Bauausfuehrung,
  sicherheit: Sicherheit,
  praevention: Praevention,
  energie: Energie,
  umwelt: Umwelt,
  anbauten: Anbauten,
  eigentum: Eigentum,
  beteiligte: Beteiligte,
  // Entwurf: Gutachten und Finanzierung sind seit E14 eigene Schritte und
  // haben noch kein eigenes Motiv — sie leihen sich vorerst das der
  // fachlich nächstliegenden Nachbarn.
  gutachten: Unterlagen,
  finanzierung: Eigentum,
  versicherungsbedarf: Summary,
  unterlagen: Unterlagen,
  summary: Summary,
};

const StepHero = ({ kind }: { kind: ImmoStepKind }) => {
  const Hero = HEROES[kind];
  return <Hero />;
};

export default StepHero;
